import React, { useEffect } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import {
  BodyText,
  DisplayText,
  Modal,
  ScrollViewWithHeaderTitle,
  theme,
  useResponsive,
} from '@sg/garnish';
import { type LineItem, type LineItemSuccess } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLineItemName } from '@order/shared-hooks';

import { AppFooter, LoadingAnimation } from '../../components';
import { ErrorFallback } from '../../components/ErrorBoundary';
import { SharedLineItem } from '../../components/SharedLineItem';
import { useReorderLineItem } from '../ReorderingScreen/hooks';
import { useGetSharedLineItemQuery } from './GraphQL/ShareLineItemScreen.generated';
import { ShareLineItemScreenV2 } from './ShareLineItemScreenV2';

// ─────────────────────────────────────────────────────────────────────────────

export const ShareLineItemScreen = () => {
  const isSharedLinkCustomizationEnabled = useFeatureFlag(
    'CELS-5505-order-shared-link-customization-enabled',
  );

  if (isSharedLinkCustomizationEnabled) {
    return <ShareLineItemScreenV2 />;
  }

  return <ShareLineItemLegacyScreen />;
};

const ShareLineItemLegacyScreen = () => {
  const { formatMessage } = useIntl();
  const { lineItemSlug } = useLocalSearchParams<ShareLineItemRouteParams>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ fetching, data }] = useGetSharedLineItemQuery({
    variables: { input: { slug: lineItemSlug } },
    requestPolicy: 'network-only',
  });

  // ─── Data ────────────────────────────────────────────────────────────

  const lineItemBySlug = data?.lineItemBySlug as LineItemSuccess;
  const error = formatMessage(messages.shareLineItemError);

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation />;
  }

  if (!lineItemBySlug?.lineItem) {
    return <ErrorFallback error={error} />;
  }

  return <ShareLineItemLegacyContentScreen lineItem={lineItemBySlug.lineItem} />;
};

const ShareLineItemLegacyContentScreen = (props: ShareLineItemLegacyContentScreenProps) => {
  const { lineItem } = props;

  const { formatMessage } = useIntl();
  const { match: matchStyle, minWidth } = useResponsive();
  const { bottom } = useSafeAreaInsets();

  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const startReorderLineItem = useReorderLineItem(lineItem);
  const productName = useLineItemName(lineItem.product, lineItem);

  const contentContainerBottomInsetStyles = useStyle(
    () => ({ paddingBottom: bottom + theme.spacing['20'] }),
    [bottom],
  );

  // ─── Derived ─────────────────────────────────────────────────────────

  const contentContainerStyles = [
    styles.grow,
    matchStyle([contentContainerBottomInsetStyles, undefined]),
  ];
  const headerStyles = matchStyle([styles.headerMobile, styles.headerDesktop]);
  const contentStyles = [styles.content, matchStyle([styles.contentMobile, undefined])];
  const productStyles = matchStyle([undefined, styles.productDesktop]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    setOptions({ title: productName });
  }, [setOptions, productName]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollViewWithHeaderTitle style={styles.body} contentContainerStyle={contentContainerStyles}>
      <Modal.Row style={headerStyles}>
        <DisplayText style={styles.title} size={3}>
          {productName}
        </DisplayText>

        <BodyText style={styles.customerName} size={3}>
          {formatMessage(messages.accountSharedBy, { name: lineItem.customerName })}
        </BodyText>
      </Modal.Row>

      <Modal.Row style={contentStyles}>
        <View style={productStyles}>
          <SharedLineItem lineItem={lineItem} onAddToBag={startReorderLineItem} />
        </View>
      </Modal.Row>

      {minWidth.isSM ? <AppFooter /> : null}
    </ScrollViewWithHeaderTitle>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  body: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  grow: {
    flexGrow: 1,
  },
  headerDesktop: {
    paddingTop: theme.spacing['4'],
    alignItems: 'center',
  },
  headerMobile: {
    alignItems: 'flex-start',
  },
  title: {
    paddingTop: theme.spacing['6'],
  },
  customerName: {
    paddingTop: theme.spacing['3'],
  },
  content: {
    alignItems: 'center',
    paddingVertical: theme.spacing['10'],
  },
  contentMobile: {
    paddingVertical: theme.spacing['6'],
  },
  productDesktop: {
    maxWidth: 380,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareLineItemError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Share Line Item | Error',
  },
  accountSharedBy: {
    defaultMessage: 'By {name}',
    description: 'Account | Shared By',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ShareLineItemRouteParams = {
  lineItemSlug: string;
};

type ShareLineItemLegacyContentScreenProps = {
  lineItem: LineItem;
};
